import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import Label from '@jll/label';
import Icon from '@jll/icon';
import styles from './text-field.scss';

const TextField = ({ type, fullWidth, className, isRequired, isDisabled, labelErrorText, rows, id, label, helpText, hasError, multiline, icon, ...other }) => {
    const InputElement = multiline ? 'textarea' : 'input';

    const getStyles = classNames.bind(styles);
    const componentMainClass = 'text-field';
    const inputClassName = getStyles(
        `${componentMainClass}__input`,
        {
            [`${componentMainClass}__input--error`]: hasError,
            [`${componentMainClass}__input--multi`]: multiline,
            [`${componentMainClass}__input--single`]: !multiline,
            [`${componentMainClass}__input--with-icon`]: icon
        },
        className
    );

    let inputElement = null;

    function onStepUpClick() {
        inputElement.stepUp();
        // eslint-disable-next-line no-undef
        inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        inputElement.focus();
    }

    function onStepDownClick() {
        inputElement.stepDown();
        // eslint-disable-next-line no-undef
        inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        inputElement.focus();
    }

    const inputAttributes = {
        id,
        className: inputClassName,
        rows: multiline ? rows || 4 : undefined,
        disabled: isDisabled,
        required: isRequired,
        type,
        ref: input => {
            inputElement = input;
        },
        ...other
    };

    const helpClassName = getStyles(`${componentMainClass}__help`, {
        [`${componentMainClass}__help--error`]: hasError
    });
    const wrapperClassName = getStyles(componentMainClass, {
        [`${componentMainClass}--full`]: fullWidth
    });
    const stepUpButtonClass = getStyles(styles['text-field__button'], styles['text-field__button--step-up']);
    const stepUpIconClass = getStyles(styles['text-field__button--step-up-icon'], 'fas fa-sort-up');
    const stepDownButtonClass = getStyles(styles['text-field__button'], styles['text-field__button--step-down']);
    const stepDownIconClass = getStyles(styles['text-field__button--step-down-icon'], 'fas fa-sort-down');
    const iconClasses = getStyles('text-field__icon', icon);

    return (
        <span className={wrapperClassName}>
            {label ? (
                <Label htmlFor={id} isRequired={isRequired} labelErrorText={labelErrorText}>
                    {label}
                </Label>
            ) : (
                ''
            )}
            <span className={styles['text-field__input-wrapper']}>
                {icon && <Icon className={iconClasses} />}
                <InputElement {...inputAttributes} />
                {type === 'number' && (
                    <Fragment>
                        <button type="button" className={stepUpButtonClass} onClick={onStepUpClick} tabIndex={-1}>
                            <Icon className={stepUpIconClass} />
                        </button>
                        <button type="button" className={stepDownButtonClass} onClick={onStepDownClick} tabIndex={-1}>
                            <Icon className={stepDownIconClass} />
                        </button>
                    </Fragment>
                )}
            </span>
            {helpText ? <span className={helpClassName}>{helpText}</span> : ''}
        </span>
    );
};

TextField.propTypes = {
    /** This property helps users to fill forms faster, especially on mobile devices.
     * The name can be confusing, as it's more like an autofill.
     * You can learn more about it here: https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill */
    autoComplete: PropTypes.string,
    /** If true, the input will be focused during the first mount. */
    autoFocus: PropTypes.bool,
    /** The CSS class name of the wrapper element. */
    className: PropTypes.string,
    /** If true, the switch will be disabled. */
    isDisabled: PropTypes.bool,
    /** Error text of the radio group */
    labelErrorText: PropTypes.string,
    /** If true, the input will indicate an error. */
    hasError: PropTypes.bool,
    /** If true, the input will take up the full width of its container. */
    fullWidth: PropTypes.bool,
    /** Help or error text that is displayed bellow input */
    helpText: PropTypes.string,
    /** The id of the input element. */
    id: PropTypes.string,
    /** Label text that is displayed above the input */
    label: PropTypes.string,
    /** If true, a textarea element will be rendered. */
    multiline: PropTypes.bool,
    /** Name attribute of the input element. */
    name: PropTypes.string,
    /** Callback fired when the value is changed. */
    onChange: PropTypes.func,
    /** Type of the input element. It should be a valid HTML5 input type. */
    type: PropTypes.string,
    /** The input value, required for a controlled component. */
    value: PropTypes.string,
    /** Show an icon on the left side of the input. Use class name for value. */
    icon: PropTypes.string
};

TextField.defaultProps = {
    autoComplete: '',
    autoFocus: false,
    className: '',
    isDisabled: false,
    labelErrorText: '(required)',
    hasError: false,
    fullWidth: false,
    helpText: '',
    id: '',
    label: '',
    multiline: false,
    name: '',
    onChange: undefined,
    type: 'text',
    value: '',
    icon: ''
};

export default TextField;
